/* eslint no-use-before-define: ["error", { "variables": false }] */

import PropTypes from 'prop-types';
import React, { lazy, Suspense } from 'react';
import { StyleSheet } from 'react-native-web';
import TouchableOpacity from './TouchableOpacity';

const MessageImageLightbox = lazy(() => import('./MessageImageLightbox'));

export default class MessageImage extends React.Component {
  state = {
    isOpen: false,
    messageIndex: 0,
    maxImageWidth: 0,
    maxImageHeight: 0,
    imageLoaded: false,
    imageWidth: 0,
    imageHeight: 0
  };

  resizeObserver = null;
  enclosingDiv = null;

  updateImageWidth = () => {
    // Use cached reference if available, otherwise get it (for robustness)
    const enclosingDiv = this.enclosingDiv || document.getElementById('idEnclosingParent');
    if (enclosingDiv) {
      const chatPageWidth = enclosingDiv.offsetWidth;
      // Fixed container height: 300px
      // Container width: 70% of chat page - 55px (avatar space)
      const avatarSpace = 55;
      const containerWidth = chatPageWidth * 0.7 - avatarSpace;
      this.setState({
        maxImageWidth: containerWidth,
        maxImageHeight: 300 // Fixed height for image container
      });
    }
  };

  componentDidMount() {
    const enclosingDiv = document.getElementById('idEnclosingParent');
    if (enclosingDiv) {
      this.enclosingDiv = enclosingDiv;

      // Initial width calculation
      this.updateImageWidth();

      // Use ResizeObserver to watch for size changes (handles screen occlusion, keyboard, etc.)
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateImageWidth();
        });
        this.resizeObserver.observe(enclosingDiv);
      } else {
        // Fallback to window resize event if ResizeObserver is not supported
        window.addEventListener('resize', this.updateImageWidth);
      }
    }
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    } else {
      window.removeEventListener('resize', this.updateImageWidth);
    }
  }

  handleImageLoad = (event) => {
    const img = event.target;
    if (img && img.naturalWidth && img.naturalHeight) {
      this.setState({
        imageLoaded: true,
        imageWidth: img.naturalWidth,
        imageHeight: img.naturalHeight
      });
    }
  };

  handleImageError = (event) => {
    const original = this.props.currentMessage.image;
    if (event.currentTarget.dataset.thumbnail === 'true') {
      event.currentTarget.dataset.thumbnail = 'false';
      event.currentTarget.src = original;
    }
  };

  onClickImage = () => {
    this.setState({ isOpen: true });
  };

  static getDerivedStateFromProps(props, state) {
    if (state.isOpen) {
      return {};
    }
    const { imageMessages, currentMessage } = props;
    const messagesIds = imageMessages.map((item) => item.id);
    const messageIndex = messagesIds.indexOf(currentMessage.id);
    return { messageIndex };
  }

  render() {
    const { imageMessages, currentMessage, containerStyle: propContainerStyle } = this.props;
    const { isOpen, messageIndex, maxImageWidth, maxImageHeight } = this.state;

    // Fixed container with cover image
    const containerStyle = {
      ...styles.container,
      ...propContainerStyle,
      width: maxImageWidth + 'px',
      height: maxImageHeight + 'px',
      overflow: 'hidden',
      borderRadius: 13
    };

    const imageStyle = {
      ...this.props.imageStyle,
      margin: 0,
      padding: 0,
      marginTop: 0,
      marginLeft: 0,
      marginRight: 0,
      width: '95%',
      height: '98%',
      objectFit: 'cover', // Image completely covers the container
      borderRadius: 0, // Border radius handled by container
      display: 'block'
    };

    const slides = (imageMessages || [])
      .map((message) => ({ src: message.image }))
      .filter((slide) => Boolean(slide.src));
    const thumbnail = currentMessage.thumbnail;

    return (
      <TouchableOpacity onPress={this.onClickImage} style={containerStyle}>
        <img
          src={thumbnail || currentMessage.image}
          data-thumbnail={Boolean(thumbnail)}
          style={imageStyle}
          onLoad={this.handleImageLoad}
          onError={this.handleImageError}
          alt='message image'
        />
        {isOpen ? (
          <Suspense fallback={null}>
            <MessageImageLightbox
              open={isOpen}
              close={() => this.setState({ isOpen: false })}
              index={messageIndex}
              slides={slides}
              onView={(index) => this.setState({ messageIndex: index })}
            />
          </Suspense>
        ) : null}
      </TouchableOpacity>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    padding: 0,
    margin: 0,
    marginTop: 0,
    marginLeft: 0,
    marginRight: 0,
    marginBottom: 6
  },
  image: {
    borderRadius: 13,
    resizeMode: 'contain',
    display: 'block'
  },
  imageActive: {
    flex: 1
  }
});

MessageImage.defaultProps = {
  currentMessage: {
    image: null
  },
  containerStyle: {},
  imageStyle: {},
  imageProps: {},
  lightboxProps: {}
};

MessageImage.propTypes = {
  currentMessage: PropTypes.object,
  //containerStyle: ViewPropTypes.style,
  //imageStyle: Image.propTypes.style,
  imageProps: PropTypes.object,
  lightboxProps: PropTypes.object
};
